import type { Locale } from '@tuic/i18n';
import { CacheTag } from '../../utils';
import { remember, serializeDataToCacheKey } from '@tuic/cache';
import { isObject } from '@tuic/lib/helpers/object';
import { Pcl } from '../../index';
import { logger } from '@tuic/logger';

export type PclAirportCollection = Record<string, PclAirport | undefined>;

export type PclAirport = {
  iata: string;
  country?: string | null;
  name: string;
};

/**
 * Returns the Airports from the PCL.
 */
export const getAirports = async (
  locale: Locale,
): Promise<PclAirportCollection | null> => {
  const { data, error } = await Pcl.getAirports({ query: { locale } });

  if (error) {
    logger.error(`[PCL] Could not fetch airports. Locale: ${locale}`);
    logger.error(error);

    return {};
  }

  let airports: PclAirportCollection = {};

  if (!isObject(data)) {
    return airports;
  }

  Object.entries(data).forEach(([iataKey, pclAirport]) => {
    if (!isObject(pclAirport)) {
      return;
    }

    let iata = 'iata' in pclAirport ? pclAirport.iata : undefined;
    let country = 'country' in pclAirport ? pclAirport.country : undefined;
    let name = 'name' in pclAirport ? pclAirport.name : undefined;

    if (!name) {
      name = iata;
    }

    airports[iataKey] = {
      iata: iata ? String(iata) : '',
      country: country ? String(country) : null,
      name: name ? String(name) : '',
    };
  });

  return airports;
};

/**
 * Returns the Airports from the PCL.
 */

export const cachedGetAirports: typeof getAirports = locale => {
  return remember(
    serializeDataToCacheKey([CacheTag.pcl, 'getAirports', locale]),
    () => getAirports(locale),
    {
      tags: [CacheTag.pcl, CacheTag.airportResource, CacheTag.getAirports],
      ttl: 600,
    },
  );
};
